import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { jobs as jobsApi } from '../utils/api';
import JobCard from '../components/JobCard';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';

const TYPES      = ['ALL','JOB','SERVICE','TASK','FREELANCE'];
const CATEGORIES = ['All','Technical','Domestic','Freelance','Tutor','Creative','Local Task'];

import LocationSelector from '../components/LocationSelector';

export default function JobsPage() {

  const { t, user, fetchBalance, walletBalance } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [jobsList, setJobs]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [appliedJobIds, setAppliedJobIds] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyForm, setApplyForm] = useState({ message: '', contact_info: '', experience_proof: '' });
  const [applying, setApplying] = useState(false);
  const [search,   setSearch]   = useState(searchParams.get('q') || '');
  const [type,     setType]     = useState('ALL');
  const [category, setCategory] = useState(searchParams.get('category') || 'All');
  const [location, setLocation] = useState(searchParams.get('location') || '');
  
  // Advanced filters
  const [minBudget, setMinBudget] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [minDeadline, setMinDeadline] = useState('');
  const [maxDeadline, setMaxDeadline] = useState('');
  const [orderBy, setOrderBy] = useState('recent');
  const [showFilters, setShowFilters] = useState(false);


  const fetchJobs = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search)             params.q        = search;
    if (type !== 'ALL')     params.type     = type;
    if (category !== 'All') params.category = category;
    if (location)           params.location = location;
    
    if (minBudget)    params.min_budget   = minBudget;
    if (maxBudget)    params.max_budget   = maxBudget;
    if (minDeadline)  params.min_deadline = minDeadline;
    if (maxDeadline)  params.max_deadline = maxDeadline;
    if (orderBy)      params.order_by     = orderBy;

    jobsApi.list(params)
      .then(r => setJobs(r.jobs ?? []))
      .catch(() => toast.error(t('error_occurred')))
      .finally(() => setLoading(false));
  }, [search, type, category, location, minBudget, maxBudget, minDeadline, maxDeadline, orderBy, t]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleApply = job => {
    if (!user) { navigate('/login'); return; }
    setSelectedJob(job);
    setShowApplyModal(true);
  };

  const confirmApply = async e => {
    if (e) e.preventDefault();
    if (!selectedJob) return;
    setApplying(true);
    try {
      await jobsApi.apply(selectedJob.id, applyForm);
      fetchBalance();
      toast.success(t('applied_success'));
      setAppliedJobIds(prev => [...prev, selectedJob.id]);
      setShowApplyModal(false);
      setApplyForm({ message: '', contact_info: '', experience_proof: '' });
    } catch (err) {
      toast.error(err.error || t('error_occurred'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="container" style={{ paddingTop: '32px', paddingBottom: '60px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '24px' }}>{t('nav_find_job')}</h1>

      {/* Filters Section */}
      <div className="card" style={{ marginBottom: '24px', padding: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '12px', marginBottom: '16px' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }}>🔍</span>
            <input 
              className="form-input"
              style={{ paddingLeft: '36px' }}
              placeholder={t('search_placeholder')} 
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchJobs()} 
            />
          </div>
          <button 
            className={`btn ${showFilters ? 'btn-primary' : 'btn-outline'}`} 
            onClick={() => setShowFilters(!showFilters)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <span>⚙️</span> {t('filters')}
          </button>
          <button className="btn btn-primary" onClick={fetchJobs}>{t('search_btn')}</button>
        </div>

        {/* Basic Filters (Always visible or in line) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: showFilters ? '16px' : '0' }}>
          <div>
            <label className="form-label" style={{ fontSize: '12px' }}>{t('category')}</label>
            <select className="form-input" value={category} onChange={e => setCategory(e.target.value)}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label" style={{ fontSize: '12px' }}>{t('location')}</label>
            <LocationSelector initialValue={location} onChange={val => setLocation(val)} />
          </div>
          <div>
            <label className="form-label" style={{ fontSize: '12px' }}>{t('order_by')}</label>
            <select className="form-input" value={orderBy} onChange={e => setOrderBy(e.target.value)}>
              <option value="recent">{t('recent')}</option>
              <option value="oldest">{t('oldest')}</option>
              <option value="rate_desc">{t('rate_desc')}</option>
              <option value="rate_asc">{t('rate_asc')}</option>
            </select>
          </div>
        </div>

        {showFilters && (
          <div className="stagger" style={{ borderTop: '1px solid var(--border)', paddingTop: '16px', marginTop: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '12px' }}>{t('type')}</label>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {TYPES.map(tp => (
                    <button key={tp} onClick={() => setType(tp)}
                      className={`chip ${type === tp ? 'chip-teal' : 'chip-gray'}`}
                      style={{ cursor: 'pointer', border: 'none', padding: '4px 10px', fontSize: '11px' }}>
                      {tp === 'ALL' ? 'All' : tp}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="form-label" style={{ fontSize: '12px' }}>{t('budget')} (৳)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input className="form-input" type="number" placeholder="Min" value={minBudget} onChange={e => setMinBudget(e.target.value)} style={{ padding: '6px 10px', fontSize: '13px' }} />
                  <span style={{ color: 'var(--text3)' }}>-</span>
                  <input className="form-input" type="number" placeholder="Max" value={maxBudget} onChange={e => setMaxBudget(e.target.value)} style={{ padding: '6px 10px', fontSize: '13px' }} />
                </div>
              </div>

              <div>
                <label className="form-label" style={{ fontSize: '12px' }}>{t('due_date_range')}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input className="form-input" type="date" value={minDeadline} onChange={e => setMinDeadline(e.target.value)} style={{ padding: '6px 10px', fontSize: '13px' }} />
                  <span style={{ color: 'var(--text3)' }}>to</span>
                  <input className="form-input" type="date" value={maxDeadline} onChange={e => setMaxDeadline(e.target.value)} style={{ padding: '6px 10px', fontSize: '13px' }} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="page-loader"><div className="spinner" /></div>
      ) : jobsList.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: '40px', marginBottom: '10px' }}>🔍</div>
          <p>{t('no_jobs_found')}</p>
        </div>
      ) : (
        <div>
          <p style={{ fontSize: '13px', color: 'var(--text3)', marginBottom: '12px' }}>{jobsList.length} results</p>
          <div className="stagger">
            {jobsList.map(job => (
              <JobCard 
                key={job.id} 
                job={job} 
                onApply={handleApply} 
                applied={appliedJobIds.includes(job.id)} 
              />
            ))}
          </div>
        </div>
      )}
      {/* Apply Modal */}
      <Modal open={showApplyModal} onClose={() => setShowApplyModal(false)} title={t('apply_for_job')}>
        <div style={{ padding: '4px' }}>
          <div className="form-group">
            <label className="form-label">{t('application_message')} *</label>
            <textarea 
              className="form-input" 
              rows={4} 
              required
              value={applyForm.message}
              onChange={e => setApplyForm({ ...applyForm, message: e.target.value })}
              placeholder="Why are you the best fit for this job?" 
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('contact_info')} (Optional)</label>
            <input 
              className="form-input" 
              value={applyForm.contact_info}
              onChange={e => setApplyForm({ ...applyForm, contact_info: e.target.value })}
              placeholder="Mobile no. or alternative email" 
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('experience_proof')} (Optional URL/Text)</label>
            <textarea 
              className="form-input" 
              rows={2}
              value={applyForm.experience_proof}
              onChange={e => setApplyForm({ ...applyForm, experience_proof: e.target.value })}
              placeholder="Link to portfolio, CV or past work examples" 
            />
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button className="btn btn-outline btn-full" onClick={() => setShowApplyModal(false)}>{t('cancel')}</button>
            <button className="btn btn-primary btn-full" onClick={confirmApply} disabled={applying || !applyForm.message}>
              {applying ? t('submitting') : `${t('submit')} (−1 token)`}
            </button>
          </div>
          <p style={{ fontSize: '11px', color: 'var(--text3)', textAlign: 'center', marginTop: '12px' }}>
            Applying costs 1 token. You have {walletBalance ?? '...'} tokens.
          </p>
        </div>
      </Modal>
    </div>
  );
}
